import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Chip, Modal, Typography } from '@heroui/react';
import { TransactionDetailHeader } from './TransactionDetailHeader';
import type { SalesTransaction } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { TransactionCartLine } from './TransactionCartLine';
import { TransactionDetailSeller } from './TransactionDetailSeller';
import { TransactionDetailTotals } from './TransactionDetailTotals';
import { TransactionDetailFooter } from './TransactionDetailFooter';
type TransactionDetailModalProps = {
    isOpen: boolean;
    onClose: () => void;
    transaction: SalesTransaction | null;
};

/**
 * Detail modal for one row. The grid in `TransactionsView` keeps the
 * full transaction payload around, so the modal reads everything from
 * the `transaction` prop — no extra fetch.
 *
 * The dialog goes full-bleed below the `sm` breakpoint via Tailwind
 * `max-sm:` overrides on `Modal.Dialog`; at `sm`+ it shrinks to the
 * standard 520px sheet.
 */
export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
    isOpen,
    onClose,
    transaction,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            cart: t('sales.widgets.transactions.modal.cart'),
            cartCount: (count: number) =>
                t('sales.widgets.transactions.modal.cartCount', { count }),
        }),
        [t],
    );

    if (!transaction) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal.Backdrop>
                <Modal.Container className="max-sm:p-0" size="md">
                    <Modal.Dialog
                        className="max-sm:m-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:p-4"
                        {...getNodeTestIdProp('transaction-detail')}
                    >
                        <TransactionDetailHeader
                            transaction={transaction}
                            onClose={onClose}
                        />
                        <Modal.Body
                            className="max-h-[60vh] overflow-y-auto"
                            {...getNodeTestIdProp('transaction-detail-body')}
                        >
                            <TransactionDetailSeller
                                transaction={transaction}
                            />

                            <div
                                className="mb-3 flex items-center gap-2"
                                {...getNodeTestIdProp(
                                    'transaction-detail-cart-heading',
                                )}
                            >
                                <Icon
                                    aria-hidden
                                    className="size-3.5 text-muted"
                                    icon="solar:cart-large-2-bold-duotone"
                                    {...getNodeTestIdProp(
                                        'transaction-detail-cart-icon',
                                    )}
                                />
                                <Typography
                                    type="body-sm"
                                    weight="semibold"
                                    {...getNodeTestIdProp(
                                        'transaction-detail-cart-title',
                                    )}
                                >
                                    {copy.cart}
                                </Typography>
                                <Chip
                                    size="sm"
                                    variant="soft"
                                    {...getNodeTestIdProp(
                                        'transaction-detail-cart-count',
                                    )}
                                >
                                    {copy.cartCount(transaction.cart.length)}
                                </Chip>
                            </div>

                            <ul
                                className="mb-4 flex flex-col gap-2"
                                {...getNodeTestIdProp(
                                    'transaction-detail-cart-list',
                                )}
                            >
                                {transaction.cart.map((item) => (
                                    <TransactionCartLine
                                        key={item.barcode}
                                        item={item}
                                    />
                                ))}
                            </ul>

                            <TransactionDetailTotals
                                transaction={transaction}
                            />

                            <TransactionDetailFooter
                                transaction={transaction}
                            />
                        </Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
